// Cartes d'agents dans le fil (P1, contrat C0 v1.15, genres « agent » et « agent_change ») :
// fonctions pures des cartes « Nouvel agent » (AgentDraftCard) et « Modification d'agent »
// (AgentChangeCard). Aucun import React : testées par node --test.
//
// Spécification « agent », émise par prepare_agent (backend/agents/carteAgent.js) :
//   {kind: "agent", version: 1, carte_id, title, lang, nom, famille, domaine,
//    resume: [textes en clair : déclencheur, observation, règle, livrable, rappel (sauf rapporteur), durée],
//    champs: {nom, seuil?, unite?, declencheur: {type, minutes? | cron? | dans_minutes? | le?}, duree: {type, date? | nombre?}},
//    canaux: [{canal, disponible, choisi?, impose?, navigateurs?}],
//    destinataires: {proprietaire: {email_masque, telephone_masque}, externes: [adresses masquées]},
//    decision?: {enjeu, action, responsable?, delai?, texte} (conduite à tenir quand l'alerte tombe),
//    avertissements: [texte], essai: {jour, retro}, doublon: {agent_id, nom, abonnes} | null,
//    degrade: null | texte | {message?, raison?}}
// Spécification « agent_change », émise par prepare_agent_change :
//   {kind: "agent_change", version: 1, carte_id, title, lang, agent_id, nom, action, version_agent,
//    avant: [{champ, libelle, valeur}], apres: [...], abonnes_prevenus, avertissements}
// La spec ne porte aucune coordonnée en clair (adresses et numéro masqués) ; la carte ne renvoie
// au serveur que ce que l'utilisateur a changé (verifierChamps), jamais la mission, qui est
// relue en base. L'écran reste en français, quelle que soit la langue de l'agent (spec.lang).
// Canaux (rectification du 26 octobre) : tous cochés par défaut, chacun émet s'il est disponible,
// sans canal de repli ni de remplacement ; l'application et l'e-mail partent toujours (cochés, non
// décochables ; le serveur les impose aussi) ; un canal indisponible est dit « indisponible pour
// le moment » (WhatsApp : « bientôt disponible ») et n'envoie rien.

import { externalAddresses, parseAddresses } from '../email/emailDraft.js';

export const NOM_MAX = 120;
export const MAX_EXTERNES = 5;
// Fréquences proposées dans la carte, dans les bornes de la mission (1 min à 24 h ; rectification
// du 27 octobre : la fréquence demandée est prise au mot) ; le déclencheur proposé s'y ajoute
// toujours, à sa place (autre intervalle) ou à la fin (calendrier, exécution unique, événement)
export const INTERVALLES = [1, 2, 5, 10, 15, 30, 60, 120, 360, 720, 1440];
const INTERVALLE_BORNES = [1, 1440];
// Ordre d'affichage des canaux. « push » (notification du navigateur) n'est monté que si la spec
// le nomme : il dépend d'une inscription du navigateur, faite depuis la cloche
export const CANAUX = ['application', 'push', 'email', 'sms', 'whatsapp'];
const CANAUX_TOUJOURS = ['application', 'email', 'sms', 'whatsapp'];
// Canaux qui partent toujours : cochés et non décochables (le serveur les impose aussi)
export const CANAUX_IMPOSES = ['application', 'email'];
export const LIBELLES_CANAUX = {
  application: 'Application',
  push: 'Notification du navigateur',
  email: 'E-mail',
  sms: 'SMS',
  whatsapp: 'WhatsApp',
};
const FAMILLES = ['veilleur', 'sentinelle', 'rapporteur', 'analyste'];
export const DUREES = [
  ['permanente', 'Sans fin'],
  ['jusqu_au', 'Jusqu’à une date'],
  ['envois', 'Nombre d’envois'],
];

// Actions d'une carte de modification : titre de la carte, effet dit avant la confirmation
// (une pause ou une suppression n'a aucune ligne avant et après à montrer) et état une fois
// appliquée
export const ACTIONS = {
  pause: { titre: 'Mise en pause de l’agent', effet: 'L’agent ne fera plus aucun contrôle jusqu’à sa reprise.', fait: 'Agent mis en pause' },
  reprise: { titre: 'Reprise de l’agent', effet: 'Les contrôles reprendront à la prochaine échéance.', fait: 'Agent repris' },
  modification: { titre: 'Modification de l’agent', effet: 'La nouvelle version s’appliquera dès le prochain contrôle.', fait: 'Modification appliquée' },
  suppression: { titre: 'Suppression de l’agent', effet: 'L’agent sera archivé : il ne contrôlera plus rien et ses abonnés ne recevront plus ses alertes.', fait: 'Agent supprimé' },
  abonnement: { titre: 'Abonnement à l’agent', effet: 'Vous recevrez les alertes de cet agent.', fait: 'Abonnement enregistré' },
  desabonnement: { titre: 'Désabonnement de l’agent', effet: 'Vous ne recevrez plus les alertes de cet agent.', fait: 'Désabonnement enregistré' },
};

// Statut d'un envoi (essai) tel que le serveur le rend (table envois, contrat §2)
const STATUTS_ENVOI = {
  en_cours: 'en cours',
  en_file: 'en file d’attente',
  envoye: 'envoyé',
  programme: 'programmé',
  livre: 'livré',
  lu: 'lu',
  echec: 'échec',
  annule: 'annulé',
  incertain: 'incertain',
  retenu: 'retenu jusqu’à 7 h (heures calmes)',
  indisponible: 'indisponible pour le moment',
};

const nf = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 });

// Texte venu du serveur (donc des données) : caractères de contrôle retirés, espaces réduits,
// longueur bornée ; jamais interprété (React l'échappe)
const str = (v, max = 200) => (typeof v === 'string' ? v.replace(/[\u0000-\u001f\u007f]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, max) : '');
const nombre = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const entier = (v) => (Number.isInteger(v) && v >= 0 ? v : null);
const textes = (liste, max, longueur) => (Array.isArray(liste) ? liste.map((t) => str(t, longueur)).filter(Boolean).slice(0, max) : []);
const memeValeur = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

/** Nombre affiché (« 2,5 », « 1 200 ») ; texte laissé tel quel. */
export const formatValeur = (v) => (typeof v === 'number' ? nf.format(v) : String(v ?? ''));

// ── Déclencheur ─────────────────────────────────────────────────────

export function texteIntervalle(minutes) {
  if (!Number.isInteger(minutes) || minutes <= 0) return 'Fréquence proposée';
  if (minutes === 1) return 'Toutes les minutes';
  if (minutes === 60) return 'Toutes les heures';
  if (minutes % 60 === 0) return `Toutes les ${minutes / 60} h`;
  return `Toutes les ${minutes} min`;
}

// Durée en minutes en clair, comme le serveur : « 5 min », « 2 h », « 1 h 30 », « 7 jours »
function dureeMinutes(n) {
  if (n >= 2880 && n % 1440 === 0) return `${n / 1440} jours`;
  if (n >= 60) return `${Math.floor(n / 60)} h${n % 60 ? ` ${String(n % 60).padStart(2, '0')}` : ''}`;
  return `${n} min`;
}

/**
 * Exécution unique en clair : « Une seule fois, 5 min après l'activation » (dans_minutes, compté
 * depuis le clic sur Activer), « Une seule fois, le 27/10 à 14:30 (heure d'Abidjan) » (le, avec
 * l'année quand elle n'est pas celle de maintenant : « le 27/10/2027 », une année de trop se
 * voit), « Une seule fois, dès l'activation ».
 */
export function texteUnique(declencheur, maintenant = Date.now()) {
  if (Number.isInteger(declencheur?.dans_minutes) && declencheur.dans_minutes > 0) return `Une seule fois, ${dureeMinutes(declencheur.dans_minutes)} après l’activation`;
  const le = typeof declencheur?.le === 'string' ? /^(\d{4})-(\d{2})-(\d{2})T(\d{2}:\d{2})$/.exec(declencheur.le) : null;
  if (le) {
    const annee = Number(le[1]) === new Date(maintenant).getUTCFullYear() ? '' : `/${le[1]}`;
    return `Une seule fois, le ${le[3]}/${le[2]}${annee} à ${le[4]} (heure d’Abidjan)`;
  }
  return 'Une seule fois, dès l’activation';
}

const JOURS = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'];

/**
 * Calendrier cron à cinq champs (heure d'Abidjan) en clair pour les formes courantes : « Chaque
 * lundi à 8 h », « Le 1er de chaque mois à 7 h », « Du lundi au vendredi à 6 h 30 » ; null
 * sinon (la carte reprend alors le résumé du serveur).
 */
export function cronEnClair(cron) {
  const champs = String(cron ?? '').trim().split(/\s+/);
  if (champs.length !== 5) return null;
  const [mi, h, jourMois, mois, jourSemaine] = champs;
  if (!/^\d{1,2}$/.test(mi) || !/^\d{1,2}$/.test(h) || mois !== '*' || Number(mi) > 59 || Number(h) > 23) return null;
  const a = `à ${Number(h)} h${Number(mi) ? ` ${mi.padStart(2, '0')}` : ''}`;
  if (jourMois === '*' && jourSemaine === '*') return `Chaque jour ${a}`;
  if (jourMois === '*' && /^[0-7]$/.test(jourSemaine)) return `Chaque ${JOURS[Number(jourSemaine)]} ${a}`;
  if (jourMois === '*' && jourSemaine === '1-5') return `Du lundi au vendredi ${a}`;
  const n = Number(jourMois);
  if (/^\d{1,2}$/.test(jourMois) && jourSemaine === '*' && n >= 1 && n <= 31) return `Le ${n === 1 ? '1er' : n} de chaque mois ${a}`;
  return null;
}

/** Déclencheur en clair ; secours : résumé du serveur, pour ce que la carte ne sait pas dire. */
export function texteDeclencheur(declencheur, secours = '') {
  if (declencheur?.type === 'intervalle') return texteIntervalle(declencheur.minutes);
  if (declencheur?.type === 'calendrier') return cronEnClair(declencheur.cron) || secours || 'Calendrier proposé';
  if (declencheur?.type === 'evenement') return secours || 'À chaque événement de la plateforme';
  if (declencheur?.type === 'unique') return texteUnique(declencheur);
  return secours || 'Déclencheur proposé';
}

/** Valeur du sélecteur : « intervalle:60 » pour un intervalle, « propose » pour le reste. */
export function valeurDeclencheur(declencheur) {
  if (declencheur?.type === 'intervalle' && Number.isInteger(declencheur.minutes) && declencheur.minutes > 0) return `intervalle:${declencheur.minutes}`;
  return declencheur && typeof declencheur === 'object' ? 'propose' : '';
}

function declencheurDepuisValeur(valeur, propose) {
  if (valeur === 'propose') return propose ?? null;
  const m = /^intervalle:(\d{1,4})$/.exec(String(valeur ?? ''));
  return m && Number(m[1]) >= INTERVALLE_BORNES[0] && Number(m[1]) <= INTERVALLE_BORNES[1] ? { type: 'intervalle', minutes: Number(m[1]) } : null;
}

// Minutes d'une option du sélecteur (les autres déclencheurs se rangent après les fréquences)
const minutesOption = (o) => (/^intervalle:\d+$/.test(o.valeur) ? Number(o.valeur.slice('intervalle:'.length)) : Infinity);

/**
 * Options du sélecteur de déclencheur : les fréquences de 1 min à 24 h, plus le déclencheur proposé
 * s'il n'en fait pas partie, toujours présent et marqué « (proposé) » : une autre fréquence à sa
 * place dans l'ordre, un calendrier, une exécution unique ou un événement à la fin ; courant :
 * valeur déjà choisie (mission appliquée), gardée dans la liste.
 */
export function optionsDeclencheur(propose, secours = '', courant = '') {
  const cle = valeurDeclencheur(propose);
  const options = INTERVALLES.map((m) => ({ valeur: `intervalle:${m}`, libelle: texteIntervalle(m) }));
  for (const valeur of [cle, courant]) {
    if (!valeur || options.some((o) => o.valeur === valeur)) continue;
    const d = declencheurDepuisValeur(valeur, propose);
    if (d) options.push({ valeur, libelle: texteDeclencheur(d, secours) });
  }
  options.sort((a, b) => minutesOption(a) - minutesOption(b));
  if (!cle) options.unshift({ valeur: '', libelle: 'Selon la mission proposée' });
  return options.map((o) => (o.valeur === cle && cle ? { ...o, libelle: `${o.libelle} (proposé)` } : o));
}

// ── Lecture de la spécification ────────────────────────────────────

function lireDeclencheur(d) {
  if (!d || typeof d !== 'object') return null;
  if (d.type === 'intervalle') return Number.isInteger(d.minutes) && d.minutes > 0 ? { type: 'intervalle', minutes: d.minutes } : null;
  if (d.type === 'calendrier') return str(d.cron, 100) ? { type: 'calendrier', cron: str(d.cron, 100) } : null;
  if (d.type === 'evenement') return { type: 'evenement', types: textes(d.types, 10, 60) };
  if (d.type === 'unique') {
    // Échéance gardée telle que la mission la porte (le serveur la relit en base à l'activation)
    if (Number.isInteger(d.dans_minutes) && d.dans_minutes > 0) return { type: 'unique', dans_minutes: d.dans_minutes };
    if (typeof d.le === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(d.le)) return { type: 'unique', le: d.le };
    return { type: 'unique' };
  }
  return null;
}

function lireDuree(d) {
  if (d?.type === 'jusqu_au' && /^\d{4}-\d{2}-\d{2}$/.test(d.date ?? '')) return { type: 'jusqu_au', date: d.date };
  if (d?.type === 'envois' && Number.isInteger(d.nombre) && d.nombre >= 1) return { type: 'envois', nombre: d.nombre };
  return { type: 'permanente' };
}

// Canaux de la carte : tous cochés par défaut (un drapeau « choisi » à faux du serveur décoche),
// l'application et l'e-mail imposés. Un canal courant absent de la spec reste proposé, coché, sans
// disponibilité connue ; WhatsApp absent : indisponible (pas encore ouvert par la plateforme). Le
// Web Push n'est monté que si la spec le nomme (il dépend d'un navigateur activé depuis la cloche).
// La raison brute d'une indisponibilité n'est jamais lue : la carte dit seulement « indisponible ».
function lireCanaux(liste) {
  const entrees = (Array.isArray(liste) ? liste : []).filter((c) => c && CANAUX.includes(c.canal));
  return CANAUX.filter((canal) => CANAUX_TOUJOURS.includes(canal) || entrees.some((c) => c.canal === canal)).map((canal) => {
    const c = entrees.find((e) => e.canal === canal) ?? null;
    const impose = CANAUX_IMPOSES.includes(canal);
    return {
      canal,
      choisi: impose || c?.choisi !== false,
      impose,
      disponible: canal === 'application' ? true : c ? c.disponible !== false : canal === 'whatsapp' ? false : null,
      navigateurs: canal === 'push' && entier(c?.navigateurs) !== null ? c.navigateurs : null,
    };
  });
}

// Essai du jour ; minimum : lecture coupée par le temps d'un contrôle, nb est un nombre minimal
// (« au moins N éléments », carteAgent.js) ; reference : sentinelle « désormais », les événements
// du jour servent de point de départ (jamais d'alerte) ; noms : nom métier des éléments
// (« freinages brusques »), « éléments » à défaut
function lireNoms(n) {
  const un = str(n?.un, 40);
  const plusieurs = str(n?.plusieurs, 40);
  return un && plusieurs ? { un, plusieurs, feminin: n.feminin === true } : null;
}

function lireEssaiJour(j) {
  if (!j || typeof j !== 'object') return null;
  const elements = (Array.isArray(j.elements) ? j.elements : [])
    .map((e) => ({ libelle: str(e?.libelle, 120), valeur: nombre(e?.valeur) ?? (str(e?.valeur, 60) || null) }))
    .filter((e) => e.libelle || e.valeur != null)
    .slice(0, 5);
  const partiel = j.partiel === true;
  return { declenche: j.declenche === true, nb: entier(j.nb) ?? elements.length, elements, partiel, minimum: partiel || j.minimum === true, reference: j.reference === true, noms: lireNoms(j.noms) };
}

function lireEssaiRetro(r) {
  if (!r || typeof r !== 'object') return null;
  if (r.indisponible) return { indisponible: ['SANS_DATES', 'BUDGET'].includes(r.indisponible) ? r.indisponible : 'AUTRE' };
  // Courbe bornée à 100 points (contrat §3.6) : les plus récents ; une valeur absente laisse un trou
  const points = (Array.isArray(r.points) ? r.points : []).slice(-100).map((p) => ({
    date: str(p?.date, 30),
    valeur: nombre(p?.valeur),
    declenche: typeof p?.declenche === 'boolean' ? p.declenche : null,
  }));
  return { periode: str(r.periode, 60), declenchements: entier(r.declenchements), seuil: nombre(r.seuil), points, partiel: r.partiel === true };
}

function lireDoublon(d) {
  if (!d || typeof d !== 'object' || d.agent_id == null || d.agent_id === '') return null;
  return { agentId: String(d.agent_id).slice(0, 80), nom: str(d.nom, NOM_MAX), abonnes: entier(d.abonnes) };
}

const DEGRADE_DEFAUT = 'certaines vérifications n’ont pas pu être faites ; les alertes chiffrées partiront quand même.';

function lireDegrade(d) {
  if (d == null || d === false) return null;
  if (typeof d === 'string') return { message: str(d, 300) || DEGRADE_DEFAUT };
  if (typeof d === 'object') return { message: str(d.message, 300) || str(d.raison, 300) || DEGRADE_DEFAUT };
  return { message: DEGRADE_DEFAUT };
}

const lireProprietaire = (p) => ({ emailMasque: str(p?.email_masque, 120), telephoneMasque: str(p?.telephone_masque, 40) });

/**
 * Destinataires masqués relus avec l'état de la carte (GET de la carte : coordonnées appliquées,
 * numéro saisi à l'activation compris) → {emailMasque, telephoneMasque}, ou null s'ils manquent
 * (la carte garde alors ceux de la proposition).
 */
export function lireDestinataires(d) {
  if (!d || typeof d !== 'object' || !d.proprietaire || typeof d.proprietaire !== 'object') return null;
  return lireProprietaire(d.proprietaire);
}

/**
 * Conduite à tenir quand l'alerte tombe (spec.decision) → {enjeu, action, responsable, delai,
 * texte} ; texte : « Si l'alerte tombe : relancer l'assureur (gestionnaire de parc, aujourd'hui) »,
 * celui du serveur, sinon recomposé ; null sans action.
 */
export function lireDecision(d) {
  if (!d || typeof d !== 'object') return null;
  const action = str(d.action, 220);
  if (!action) return null;
  const responsable = str(d.responsable, 60);
  const delai = str(d.delai, 60);
  const qui = [responsable, delai].filter(Boolean);
  const debut = /^\p{Lu}\p{Ll}/u.test(action) ? action[0].toLowerCase() + action.slice(1) : action;
  return {
    enjeu: str(d.enjeu, 220),
    action,
    responsable,
    delai,
    texte: str(d.texte, 400) || `Si l'alerte tombe : ${debut.replace(/[.\s]+$/, '')}${qui.length ? ` (${qui.join(', ')})` : ''}`,
  };
}

/** Spécification « agent » → modèle d'affichage de la carte ; null si ce n'en est pas une. */
export function normalizeAgentSpec(raw) {
  if (!raw || typeof raw !== 'object' || raw.kind !== 'agent') return null;
  const champs = raw.champs && typeof raw.champs === 'object' ? raw.champs : {};
  const destinataires = raw.destinataires && typeof raw.destinataires === 'object' ? raw.destinataires : {};
  const proprietaire = destinataires.proprietaire && typeof destinataires.proprietaire === 'object' ? destinataires.proprietaire : {};
  const essai = raw.essai && typeof raw.essai === 'object' ? raw.essai : {};
  const nom = str(champs.nom, NOM_MAX) || str(raw.nom, NOM_MAX);
  return {
    carteId: str(raw.carte_id, 200) || null,
    title: str(raw.title) || (nom ? `Nouvel agent : ${nom}` : 'Nouvel agent'),
    lang: raw.lang === 'en' ? 'en' : 'fr',
    nom,
    famille: FAMILLES.includes(raw.famille) ? raw.famille : null,
    resume: textes(raw.resume, 8, 300),
    champs: {
      nom,
      seuil: nombre(champs.seuil),
      unite: str(champs.unite, 40),
      declencheur: lireDeclencheur(champs.declencheur),
      duree: lireDuree(champs.duree),
    },
    canaux: lireCanaux(raw.canaux),
    proprietaire: lireProprietaire(proprietaire),
    externes: textes(destinataires.externes, MAX_EXTERNES, 120),
    decision: lireDecision(raw.decision),
    avertissements: textes(raw.avertissements, 6, 300),
    essaiJour: lireEssaiJour(essai.jour),
    essaiRetro: lireEssaiRetro(essai.retro),
    doublon: lireDoublon(raw.doublon),
    degrade: lireDegrade(raw.degrade),
  };
}

// Mission appliquée renvoyée par le serveur (GET de la carte) : lue sous la forme des champs de
// la carte ou d'une mission v1 (règle imbriquée) ; seuls les champs lisibles l'emportent
function lireAppliquee(m) {
  if (!m || typeof m !== 'object') return null;
  const c = m.champs && typeof m.champs === 'object' ? m.champs : m;
  const regle = m.regle && typeof m.regle === 'object' ? m.regle : {};
  const canaux = Array.isArray(c.canaux ?? m.canaux)
    ? CANAUX.filter((canal) => (c.canaux ?? m.canaux).some((x) => (typeof x === 'string' ? x : x?.canal) === canal))
    : [];
  const duree = c.duree ?? m.duree;
  return {
    nom: str(c.nom ?? m.nom, NOM_MAX),
    seuil: nombre(c.seuil) ?? nombre(regle.seuil),
    declencheur: lireDeclencheur(c.declencheur ?? m.declencheur),
    duree: duree ? lireDuree(duree) : null,
    canaux: canaux.length ? canaux : null,
  };
}

const saisieNombre = (n) => String(n).replace('.', ',');

/** Canaux cochés complétés des canaux imposés (application, e-mail), dans l'ordre d'affichage. */
export const avecImposes = (canaux) => CANAUX.filter((c) => CANAUX_IMPOSES.includes(c) || (canaux ?? []).includes(c));

/**
 * État modifiable de la carte : valeurs proposées par la spec, ou celles de la mission appliquée
 * (carte déjà activée, relue sur le serveur) qui l'emportent champ par champ.
 * {nom, seuil (texte saisi), declencheur (valeur du sélecteur), duree: {type, date, nombre},
 *  canaux: [canal], telephone (nouveau numéro, vide = numéro enregistré),
 *  externesGardes: [rang des adresses proposées gardées], externesNouveaux (texte saisi)}
 */
export function brouillonDepuisSpec(spec, appliquee = null) {
  const propose = spec?.champs ?? {};
  const a = lireAppliquee(appliquee);
  const seuil = a?.seuil ?? propose.seuil ?? null;
  const duree = a?.duree ?? propose.duree ?? { type: 'permanente' };
  return {
    nom: a?.nom || propose.nom || '',
    seuil: seuil == null ? '' : saisieNombre(seuil),
    declencheur: valeurDeclencheur(a?.declencheur ?? propose.declencheur),
    duree: { type: duree.type, date: duree.date ?? '', nombre: duree.nombre != null ? String(duree.nombre) : '' },
    canaux: avecImposes(a?.canaux ?? (spec?.canaux ?? []).filter((c) => c.choisi).map((c) => c.canal)),
    telephone: '',
    externesGardes: (spec?.externes ?? []).map((_, i) => i),
    externesNouveaux: '',
  };
}

/** Nombre saisi (« 3 », « 2,5 », « 1 200 ») → nombre, ou null. */
export function lireNombreSaisi(texte) {
  // \s couvre aussi les espaces insécables que met Intl fr-FR (« 1 200 »)
  const t = String(texte ?? '').replace(/\s/g, '').replace(',', '.');
  return /^-?\d+(?:\.\d+)?$/.test(t) ? Number(t) : null;
}

/**
 * Numéro saisi → E.164 (« +2250708091011 ») ou null ; séparateurs retirés, « 00 » initial lu « + » ;
 * 10 chiffres sans indicatif (numéro ivoirien, forme de la plateforme) complétés de +225, comme
 * le serveur.
 */
export function numeroE164(texte) {
  let t = String(texte ?? '').replace(/[\s.()-]/g, '');
  if (t.startsWith('00')) t = `+${t.slice(2)}`;
  if (/^\d{10}$/.test(t)) t = `+225${t}`;
  else if (/^225\d{10}$/.test(t)) t = `+${t}`;
  return /^\+[1-9]\d{7,14}$/.test(t) ? t : null;
}

/** Le numéro de téléphone sert au SMS et à WhatsApp. */
export const besoinTelephone = (canaux) => (canaux ?? []).includes('sms') || (canaux ?? []).includes('whatsapp');

/**
 * Numéro saisi dans la carte → {telephone, erreur} : vide → {telephone: null} (le numéro
 * enregistré, s'il y en a un, reste ; sans numéro, les SMS ne partent simplement pas) ; invalide →
 * erreur à montrer.
 */
export function lireTelephoneSaisi(texte) {
  const saisi = String(texte ?? '').trim();
  if (!saisi) return { telephone: null, erreur: null };
  const telephone = numeroE164(saisi);
  return telephone
    ? { telephone, erreur: null }
    : { telephone: null, erreur: { champ: 'telephone', texte: 'Téléphone : numéro au format international, par exemple +225 07 08 09 10 11.' } };
}

/** Jour civil AAAA-MM-JJ à Abidjan (UTC+0, sans heure d'été) : borne de la date de fin. */
export const jourCivil = (maintenant = Date.now()) => new Date(maintenant).toISOString().slice(0, 10);

/**
 * Contrôle avant l'activation → {erreurs: [{champ, texte}], payload, horsEntreprise}.
 * payload : corps de POST /api/agents sans carte_id, avec SEULEMENT ce qui diffère de la
 * proposition : {champs?: {nom?, seuil?, declencheur?, duree?}, canaux?, telephone?,
 * destinataires_externes?}. destinataires_externes : liste complète ; une adresse proposée
 * gardée y figure par son rang dans la spec (le serveur seul la connaît en clair), une nouvelle
 * adresse en clair ; toutes les proposées retirées, la liste ne porte que des adresses.
 * horsEntreprise : nouvelles adresses hors du domaine de l'utilisateur (avertissement).
 */
export function verifierChamps(brouillon, spec, { userEmail = null, maintenant = Date.now() } = {}) {
  const erreurs = [];
  const erreur = (champ, texte) => erreurs.push({ champ, texte });
  const propose = spec?.champs ?? {};
  const champs = {};

  const nom = str(brouillon.nom, 1000);
  if (!nom) erreur('nom', 'Nom : obligatoire.');
  else if (nom.length > NOM_MAX) erreur('nom', `Nom : ${NOM_MAX} caractères au plus.`);
  else if (nom !== propose.nom) champs.nom = nom;

  // Seuil : seulement si la règle proposée en a un
  if (propose.seuil != null) {
    const seuil = lireNombreSaisi(brouillon.seuil);
    if (seuil == null) erreur('seuil', 'Seuil : un nombre, par exemple 3 ou 2,5.');
    else if (seuil !== propose.seuil) champs.seuil = seuil;
  }

  if (brouillon.declencheur) {
    const declencheur = declencheurDepuisValeur(brouillon.declencheur, propose.declencheur);
    if (!declencheur) erreur('declencheur', 'Déclencheur : choisissez une fréquence.');
    else if (!memeValeur(declencheur, propose.declencheur)) champs.declencheur = declencheur;
  }

  const d = brouillon.duree ?? { type: 'permanente' };
  let duree = { type: 'permanente' };
  if (d.type === 'jusqu_au') {
    const aujourdhui = jourCivil(maintenant);
    duree = null;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(d.date ?? '')) erreur('duree', 'Durée : choisissez la date de fin.');
    else if (d.date < aujourdhui) erreur('duree', 'Durée : la date de fin est passée.');
    else duree = { type: 'jusqu_au', date: d.date };
  } else if (d.type === 'envois') {
    const n = Number(String(d.nombre ?? '').trim());
    duree = Number.isInteger(n) && n >= 1 && n <= 1000 ? { type: 'envois', nombre: n } : null;
    if (!duree) erreur('duree', 'Durée : un nombre d’envois entre 1 et 1 000.');
  }
  if (duree && !memeValeur(duree, propose.duree ?? { type: 'permanente' })) champs.duree = duree;

  // Application et e-mail toujours cochés (le serveur les impose de même)
  const choisis = avecImposes(brouillon.canaux);
  const canauxProposes = avecImposes((spec?.canaux ?? []).filter((c) => c.choisi).map((c) => c.canal));

  // Numéro facultatif : saisi, il doit être valide ; absent, les SMS ne partent pas (aucun autre
  // canal ne les remplace) et l'agent s'active quand même
  let telephone = null;
  if (besoinTelephone(choisis)) {
    const lu = lireTelephoneSaisi(brouillon.telephone);
    if (lu.erreur) erreur(lu.erreur.champ, lu.erreur.texte);
    telephone = lu.telephone;
  }

  const proposees = spec?.externes ?? [];
  const gardees = [...new Set((brouillon.externesGardes ?? []).filter((i) => Number.isInteger(i) && i >= 0 && i < proposees.length))].sort((a, b) => a - b);
  const { valid, invalid } = parseAddresses(brouillon.externesNouveaux);
  if (invalid.length) erreur('externes', `Adresses externes : adresse invalide « ${invalid[0]} ».`);
  else if (gardees.length + valid.length > MAX_EXTERNES) erreur('externes', `Adresses externes : ${MAX_EXTERNES} au plus.`);

  const payload = {};
  if (Object.keys(champs).length) payload.champs = champs;
  if (!memeValeur(choisis, canauxProposes)) payload.canaux = choisis;
  if (telephone) payload.telephone = telephone;
  if (valid.length || gardees.length !== proposees.length) payload.destinataires_externes = [...gardees, ...valid];
  return { erreurs, payload, horsEntreprise: externalAddresses(valid, userEmail) };
}

// ── Canaux ──────────────────────────────────────────────────────────

/**
 * Note d'un canal dans la carte, ou null : indisponible (« Indisponible pour le moment. » ;
 * WhatsApp « Bientôt disponible. »), sans aucun canal de remplacement ; e-mail « Toujours
 * envoyé. » ; Web Push : navigateurs activés, ou l'invitation à l'activer depuis la cloche.
 */
export function noteCanal(entree) {
  if (!entree || entree.canal === 'application') return null;
  if (entree.disponible === false) return entree.canal === 'whatsapp' ? 'Bientôt disponible.' : 'Indisponible pour le moment.';
  if (entree.canal === 'email') return 'Toujours envoyé.';
  if (entree.canal === 'push' && entree.navigateurs === 0) return 'Activez-le pour ce navigateur depuis la cloche des notifications.';
  if (entree.canal === 'push' && entree.navigateurs > 0) return `Activé sur ${nf.format(entree.navigateurs)} navigateur${entree.navigateurs > 1 ? 's' : ''}.`;
  return null;
}

// ── Essais ──────────────────────────────────────────────────────────

/**
 * Essai sur les données du jour en une phrase, au nom métier des éléments (« 3 véhicules ») ;
 * essai coupé par le temps : « au moins N » ; sentinelle « désormais » (reference) : les
 * événements du jour servent de point de départ, seuls les suivants seront signalés.
 */
export function texteEssaiJour(jour) {
  if (!jour) return null;
  const noms = jour.noms ?? { un: 'élément', plusieurs: 'éléments', feminin: false };
  const e = noms.feminin ? 'e' : '';
  if (jour.reference) {
    if (!jour.nb) return `${noms.feminin ? 'Aucune' : 'Aucun'} ${noms.un} aujourd’hui pour l’instant : l’agent signalera chaque ${noms.feminin ? 'nouvelle' : 'nouveau'} dès son activation.`;
    const plusieurs = jour.nb > 1;
    const sujet = plusieurs ? (noms.feminin ? 'Elles servent' : 'Ils servent') : noms.feminin ? 'Elle sert' : 'Il sert';
    return `Aujourd’hui : ${nf.format(jour.nb)} ${plusieurs ? noms.plusieurs : noms.un} déjà enregistré${e}${plusieurs ? 's' : ''}. ${sujet} de point de départ : seul${e}s les suivant${e}s seront signalé${e}s.`;
  }
  if (!jour.declenche) return jour.minimum ? 'Aujourd’hui, l’agent ne se déclencherait pas sur les données lues.' : 'Aujourd’hui, l’agent ne se déclencherait pas.';
  return `Aujourd’hui, l’agent se déclencherait : ${jour.minimum ? 'au moins ' : ''}${nf.format(jour.nb)} ${jour.nb > 1 ? noms.plusieurs : noms.un}.`;
}

export function texteRetroIndisponible(code) {
  if (code === 'SANS_DATES') return 'Essai rétroactif impossible : les outils ne lisent pas le passé.';
  if (code === 'BUDGET') return 'Essai rétroactif interrompu : le temps prévu pour les essais est épuisé.';
  return 'Essai rétroactif indisponible.';
}

// Comparaisons d'une règle chiffrée (mission P0 : >, >=, <, <=, =)
const COMPARAISONS = {
  '>': (v, s) => v > s,
  '>=': (v, s) => v >= s,
  '<': (v, s) => v < s,
  '<=': (v, s) => v <= s,
  '=': (v, s) => v === s,
};

// Comparaisons essayées après « > » et la règle par élément (« = » n'y est pas : un petit
// compte d'égalités coïncide trop souvent avec le nombre annoncé)
const SECONDAIRES = ['>=', '<', '<='];

/**
 * Courbe de l'essai rétroactif → {valeurs, declenchements, seuil, echelle} pour Sparkline.
 * Points marqués : drapeau `declenche` du serveur quand il est là. Sinon (la spec v1.15 ne porte
 * que {date, valeur}, ni la comparaison de la règle), ils sont déduits du nombre de
 * déclenchements annoncé, par les lectures de la règle de la plus probable à la moins probable :
 * valeur au-delà du seuil (comparaison par défaut de la mission P0, règle sur une valeur
 * globale) ; valeur non nulle (règle par élément : la valeur compte les éléments en
 * dépassement, le seuil s'applique à chacun) ; enfin « au moins », « moins de », « au plus »,
 * retenues seulement si elles marquent toutes les mêmes points. La ligne du seuil n'est dessinée
 * que si une comparaison au seuil explique les déclenchements : jamais un repère trompeur.
 * echelle : 'regle' (valeurs dans l'unité de la règle), 'elements' (nombres d'éléments) ou null
 * (inconnue : rien ne dit ce que la courbe compte).
 */
export function pointsRetro(retro) {
  const points = Array.isArray(retro?.points) ? retro.points : [];
  const valeurs = points.map((p) => p.valeur);
  const seuil = typeof retro?.seuil === 'number' ? retro.seuil : null;
  const chiffres = points.filter((p) => typeof p.valeur === 'number');
  const aucun = { valeurs, declenchements: null, seuil: null, echelle: null };

  if (chiffres.some((p) => typeof p.declenche === 'boolean')) {
    const declenchements = points.map((p) => p.declenche === true);
    const accord = seuil != null && Object.values(COMPARAISONS).some((test) => chiffres.every((p) => (p.declenche === true) === test(p.valeur, seuil)));
    return { valeurs, declenchements, seuil: accord ? seuil : null, echelle: accord ? 'regle' : null };
  }
  const annonce = retro?.declenchements;
  if (annonce == null || !chiffres.length) return aucun;
  const marquer = (test) => points.map((p) => typeof p.valeur === 'number' && test(p.valeur));
  const retrouve = (marques) => marques.filter(Boolean).length === annonce;
  const auSeuil = (marques) => ({ valeurs, declenchements: marques, seuil, echelle: 'regle' });

  if (seuil != null) {
    const audela = marquer((v) => v > seuil);
    if (retrouve(audela)) return auSeuil(audela);
  }
  const nonNuls = marquer((v) => v > 0);
  if (retrouve(nonNuls)) return { valeurs, declenchements: nonNuls, seuil: null, echelle: 'elements' };
  if (seuil == null) return aucun;
  const autres = SECONDAIRES.map((c) => marquer((v) => COMPARAISONS[c](v, seuil))).filter(retrouve);
  // « au moins 3 » et « moins de 3 » donnent parfois le même compte sur des points différents :
  // rien n'est marqué plutôt qu'un repère au hasard
  return autres.length && autres.every((m) => memeValeur(m, autres[0])) ? auSeuil(autres[0]) : aucun;
}

/** Phrase visible de l'essai rétroactif : « Sur 8 semaines : 6 déclenchements. » */
export function texteRetro(retro) {
  if (!retro || retro.indisponible) return null;
  const n = retro.points.filter((p) => typeof p.valeur === 'number').length;
  const periode = retro.periode || `${nf.format(n)} contrôle${n > 1 ? 's' : ''} rejoué${n > 1 ? 's' : ''}`;
  if (retro.declenchements == null) return `Sur ${periode} : courbe des valeurs observées.`;
  const k = retro.declenchements;
  return `Sur ${periode} : ${k ? `${nf.format(k)} déclenchement${k > 1 ? 's' : ''}` : 'aucun déclenchement'}.`;
}

/**
 * Résumé de la courbe pour les lecteurs d'écran (le dessin est aria-hidden) : nombre de
 * contrôles, valeurs extrêmes (avec l'unité de la règle seulement quand elles y sont), seuil
 * dessiné, points marqués.
 */
export function descriptionCourbe(retro, unite = '') {
  const { valeurs, declenchements, seuil, echelle } = pointsRetro(retro);
  const chiffres = valeurs.filter((v) => typeof v === 'number');
  if (chiffres.length < 2) return '';
  const u = unite && echelle === 'regle' ? ` ${unite}` : '';
  const quoi = echelle === 'elements' ? 'éléments en dépassement' : 'valeurs';
  const parts = [
    `Courbe de ${nf.format(chiffres.length)} contrôles rejoués, ${quoi} de ${nf.format(Math.min(...chiffres))} à ${nf.format(Math.max(...chiffres))}${u}`,
  ];
  if (seuil != null) parts.push(`seuil à ${nf.format(seuil)}${u}`);
  // Aucun point marqué : la phrase visible le dit déjà (« aucun déclenchement »)
  const k = declenchements ? declenchements.filter(Boolean).length : 0;
  if (k) parts.push(`${nf.format(k)} point${k > 1 ? 's' : ''} de déclenchement marqué${k > 1 ? 's' : ''}`);
  return `${parts.join(', ')}.`;
}

/**
 * essai_envoye du serveur (liste, {envois}, booléen ou date) → {envois: [{canal, statut,
 * redirige?}]} ou null ; redirige: true seulement pour un e-mail remis aux adresses de test de la
 * plateforme (phase d'essai).
 */
export function lireEssaiEnvoye(x) {
  if (!x) return null;
  const liste = Array.isArray(x) ? x : Array.isArray(x.envois) ? x.envois : [];
  return {
    envois: liste
      .filter((e) => e && CANAUX.includes(e.canal))
      .map((e) => ({ canal: e.canal, statut: typeof e.statut === 'string' ? e.statut : null, ...(e.canal === 'email' && e.redirige === true ? { redirige: true } : {}) })),
  };
}

// Phase d'essai de la plateforme : l'e-mail part vers les adresses de test de l'équipe, jamais
// citées ; rien ne dit que le destinataire demandé l'a reçu
export const TEXTE_EMAIL_REDIRIGE = "E-mail redirigé vers les adresses de test de l'équipe pendant la phase d'essai.";
/** Vrai si l'e-mail de l'essai lu par lireEssaiEnvoye a été redirigé vers les adresses de test. */
export const essaiRedirige = (essai) => Boolean(essai?.envois?.some((e) => e.canal === 'email' && e.redirige === true));

/** Statut d'un envoi d'essai en clair ; sans coordonnée : selon le canal (numéro, navigateur, adresse). */
export function texteStatutEnvoi(statut, canal = null) {
  if (statut === 'sans_coordonnee') return canal === 'push' ? 'aucun navigateur activé' : canal === 'email' ? 'aucune adresse connue' : 'aucun numéro enregistré';
  return STATUTS_ENVOI[statut] ?? 'demandé';
}

/** Message d'essai encore en route (en cours, en file) : la carte relit son état. */
export const essaiEnCours = (essai) => Boolean(essai?.envois?.some((e) => e.statut === 'en_cours' || e.statut === 'en_file'));

// ── États tenus par le serveur ─────────────────────────────────────

// proposee | activee | rejointe | annulee | appliquee (contrat §2), plus « introuvable » : carte
// inconnue du serveur (expirée après 30 jours), connue de l'interface seulement
const VERROUILLES = ['activee', 'rejointe', 'annulee', 'appliquee', 'introuvable'];
export const estVerrouille = (statut) => VERROUILLES.includes(statut);

/** Badge d'état de la carte : {tone, text} (tons du composant Badge). */
export function etatTexte(statut, { genre = 'agent', action = 'modification' } = {}) {
  if (statut === 'introuvable') return { tone: 'warn', text: 'Proposition expirée' };
  if (statut === 'annulee') return { tone: 'neutral', text: genre === 'agent_change' ? 'Modification annulée' : 'Proposition annulée' };
  if (genre === 'agent_change') {
    if (statut === 'appliquee') return { tone: 'success', text: (ACTIONS[action] ?? ACTIONS.modification).fait };
    return { tone: 'neutral', text: 'En attente de confirmation' };
  }
  if (statut === 'activee') return { tone: 'success', text: 'Agent activé' };
  if (statut === 'rejointe') return { tone: 'success', text: 'Abonné à la veille existante' };
  // Proposition : jamais « actif » tant que le clic n'a pas eu lieu
  return { tone: 'neutral', text: 'Pas encore activé' };
}

/**
 * Message d'un appel refusé, pour la carte : service des agents indisponible (503), coupure
 * (statut 0 ; l'activation est idempotente par carte, réessayer ne crée jamais deux agents),
 * sinon la phrase du serveur ; jamais un « HTTP 502 » brut d'un intermédiaire.
 */
export function messageErreur(error, defaut) {
  if (error?.status === 503 || error?.code === 'AGENTS_INDISPONIBLES') return 'Le service des agents est momentanément indisponible : réessayez dans un instant.';
  if (error?.status === 0) return 'Connexion au serveur interrompue : réessayez dans un instant.';
  const message = typeof error?.message === 'string' ? error.message : '';
  return message && !/^HTTP \d+$/.test(message) ? message : defaut;
}

/** Identifiant d'un agent dans une réponse ({agent: {id | agent_id}}). */
export const idAgent = (agent) => {
  const id = agent?.id ?? agent?.agent_id ?? null;
  return id == null || id === '' ? null : String(id);
};

// ── Carte de modification ──────────────────────────────────────────

function valeurTexte(v) {
  if (typeof v === 'number') return formatValeur(v);
  if (typeof v === 'boolean') return v ? 'oui' : 'non';
  if (Array.isArray(v)) return v.map(valeurTexte).filter(Boolean).join(', ');
  return str(v, 300);
}

/**
 * Spécification « agent_change » → modèle d'affichage ; null si ce n'en est pas une.
 * lignes : un champ par ligne, avant et après réunis ({champ, libelle, avant, apres}).
 */
export function normalizeAgentChangeSpec(raw) {
  if (!raw || typeof raw !== 'object' || raw.kind !== 'agent_change') return null;
  const action = Object.hasOwn(ACTIONS, raw.action) ? raw.action : 'modification';
  const lire = (liste) => (Array.isArray(liste) ? liste : [])
    .filter((l) => l && typeof l === 'object' && str(l.champ, 60))
    .map((l) => ({ champ: str(l.champ, 60), libelle: str(l.libelle, 80) || str(l.champ, 60), valeur: valeurTexte(l.valeur) }))
    .slice(0, 20);
  const avant = lire(raw.avant);
  const apres = lire(raw.apres);
  const lignes = [...new Set([...avant, ...apres].map((l) => l.champ))].map((champ) => {
    const a = avant.find((l) => l.champ === champ);
    const b = apres.find((l) => l.champ === champ);
    return { champ, libelle: (b ?? a).libelle, avant: a?.valeur || '—', apres: b?.valeur || '—' };
  });
  const nom = str(raw.nom, NOM_MAX);
  return {
    carteId: str(raw.carte_id, 200) || null,
    title: str(raw.title) || `${ACTIONS[action].titre}${nom ? ` : ${nom}` : ''}`,
    lang: raw.lang === 'en' ? 'en' : 'fr',
    agentId: raw.agent_id != null && raw.agent_id !== '' ? String(raw.agent_id).slice(0, 80) : null,
    nom,
    action,
    versionAgent: entier(raw.version_agent),
    lignes,
    abonnesPrevenus: entier(raw.abonnes_prevenus) ?? 0,
    avertissements: textes(raw.avertissements, 6, 300),
  };
}

/** « 2 abonnés seront prévenus de ce changement. » ; null sans abonné à prévenir. */
export function texteAbonnesPrevenus(n) {
  if (!n) return null;
  return n === 1 ? 'L’abonné de cet agent sera prévenu de ce changement.' : `Les ${nf.format(n)} abonnés de cet agent seront prévenus de ce changement.`;
}
